import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';

import ParamsEditor from './ParamsEditor';
import SectionsEditor from './SectionsEditor';
import RawSettings, { replaceDefinition, replaceSetting } from './RawSettings';
import { TextSetting } from './BlockSettings';
import { Actions, Choice, Row, Section, SettingsFind, TextInput, TextSizeSetting } from './settingsKit';
import FilterForm from './FilterForm';
import { settingsOf } from './LensSettings';
import { pageBlocks } from './editor';
import { humanise, presetLabel } from './format';
import { type LensDocument, type LensDocumentData, isTabs } from './types';
import type { BoardEditor } from './useBoardEdit';

const OFFERED = ['today', '1d', '7d', '28d', '90d', '13w', '52w', 'mtd', 'qtd', 'ytd', 'last_week', 'last_quarter', '12m'];

const COMPARED: Array<[string, string]> = [
  ['prior', 'Prior'],
  ['ly', 'Last year'],
  ['l2y', 'Two years ago'],
  ['prior,ly', 'Both'],
  ['none', 'None'],
];

type LensPanelProps = { document: LensDocument; data?: LensDocumentData; editor: BoardEditor; dataPanel?: React.ReactNode };

export default function LensPanel({ document, data, editor, dataPanel }: LensPanelProps) {
  const [filtering, setFiltering] = useState<string>();
  const settings = settingsOf(document);
  const display = settings.filter((setting) => setting.label !== 'Filters');
  const placement = settings.find((setting) => setting.label === 'Filters');
  const labels = new Map(
    Object.values(data?.queries ?? {}).flatMap((envelope) =>
      envelope.columns.map((column) => [column.name, column.label] as const),
    ),
  );
  const tabs = document.page.find(isTabs);
  const lensSet = (patch: Record<string, unknown>) => editor.apply({ op: 'set', id: 'lens', patch });
  const compared = document.period.compare;
  const compareValues = compared.filter((entry): entry is 'prior' | 'ly' | 'l2y' => typeof entry === 'string');
  const compare = compareValues.length === compared.length ? compareValues.join(',') || 'none' : 'custom';
  const compareChoices: Array<[string, string]> = COMPARED.some(([value]) => value === compare)
    ? COMPARED
    : [[compare, compare === 'custom' ? 'Custom comparison' : `Current: ${compareValues.map((value) => COMPARED.find(([key]) => key === value)?.[1] ?? value).join(' + ')}`], ...COMPARED];

  const blocks = pageBlocks(document.page);
  const changedDisplay = display.filter((setting) => setting.value !== setting.choices[0][0]).map((setting) => `${setting.label} ${setting.choices.find(([value]) => value === setting.value)?.[1] ?? ''}`.toLowerCase());

  return (
    <Box data-lens-lens-panel sx={{ px: 2, pb: 2, pt: 1.5 }}>
      <Stack data-setting-key="root:Lens title" data-setting-row="Lens title" data-setting-section="data" spacing={0.5} sx={{ mb: 1 }}>
        <TextInput label="Lens title" onCommit={(title) => title && title !== document.title && lensSet({ title })} size="title" value={document.title} />
      </Stack>
      <SettingsFind>
        <Section defaultOpen id="lens-display" summary={changedDisplay.join(', ')} title="Display">
          {display.map((setting) => (
            <Choice choices={setting.choices} disabled={editor.pending} key={setting.label} label={setting.label} onChange={(value) => editor.apply(setting.operations(value))} value={setting.value} />
          ))}
          <TextSizeSetting disabled={editor.pending} onChange={(next) => lensSet({ display: { textSize: next === 'medium' ? null : next } })} value={document.display.textSize} />
        </Section>
        <Section id="lens-period" summary={`${presetLabel(document.period.default)}, ${document.period.presets.length} offered`} title="Period">
          <Row keywords={OFFERED.map(presetLabel)} label="Periods offered" stacked>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {[...new Set([...OFFERED, ...document.period.presets])].map((preset) => {
                const on = document.period.presets.includes(preset);
                return (
                  <Chip
                    color={on ? 'primary' : 'default'}
                    disabled={editor.pending || (on && preset === document.period.default)}
                    key={preset}
                    label={presetLabel(preset)}
                    onClick={() => lensSet({ period: { presets: on ? document.period.presets.filter((entry) => entry !== preset) : [...document.period.presets, preset] } })}
                    size="small"
                    sx={{ fontSize: 12.5 }}
                    variant={on ? 'filled' : 'outlined'}
                  />
                );
              })}
            </Stack>
          </Row>
          <Choice
            choices={compareChoices}
            disabled={editor.pending}
            label="Compared with"
            onChange={(value) => value !== compare && lensSet({ period: { compare: value === 'none' ? [] : value.split(',') } })}
            value={compare}
          />
          <Choice
            choices={[
              ['day', 'Day'],
              ['week', 'Week'],
              ['month', 'Month'],
            ]}
            disabled={editor.pending}
            label="Counts whole"
            onChange={(value) => lensSet({ period: { complete: value } })}
            value={document.period.complete ?? 'day'}
          />
          {document.period.presets.length > 1 ? (
            <Choice choices={document.period.presets.map((preset) => [preset, presetLabel(preset)])} disabled={editor.pending} label="Opens on" onChange={(value) => lensSet({ period: { default: value } })} value={document.period.default} />
          ) : null}
        </Section>
        <Section id="lens-filters" summary={document.filters.length ? document.filters.map((filter) => filter.label ?? labels.get(filter.column) ?? humanise(filter.column)).join(', ') : 'none'} title="Filters">
          {document.filters.length && placement ? (
            <Choice choices={placement.choices} disabled={editor.pending} label="Placement" onChange={(value) => editor.apply(placement.operations(value))} value={placement.value} />
          ) : null}
          {document.filters.length ? (
            <Stack component="ul" spacing={0} sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {document.filters.map((filter) => {
                const name = filter.label ?? labels.get(filter.column) ?? humanise(filter.column);
                return (
                  <Stack alignItems="center" component="li" direction="row" justifyContent="space-between" key={filter.column} sx={{ borderRadius: 1, mx: -1, px: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                    <Button onClick={() => setFiltering(filtering === filter.column ? undefined : filter.column)} size="small" sx={{ color: 'text.primary', flex: 1, fontSize: 13, justifyContent: 'flex-start', px: 0, textTransform: 'none' }} variant="text">
                      {name}
                    </Button>
                    <Tooltip title="Remove this filter">
                      <span>
                        <IconButton aria-label={`Remove the ${name} filter`} disabled={editor.pending} onClick={() => editor.apply({ op: 'filter', column: filter.column, def: null })} size="small">
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                );
              })}
            </Stack>
          ) : null}
          {filtering !== undefined ? (
            <FilterForm
              blockCount={blocks.length}
              data={data}
              editor={editor}
              filter={document.filters.find((filter) => filter.column === filtering)}
              key={filtering}
              onDone={() => setFiltering(undefined)}
              taken={document.filters.map((filter) => filter.column)}
            />
          ) : null}
          {filtering === '' ? null : (
            <Actions>
              <Button disabled={editor.pending} onClick={() => setFiltering('')} size="small" startIcon={<AddIcon />} variant="text">
                Add a filter
              </Button>
            </Actions>
          )}
        </Section>
        <Section id="lens-tabs" summary={tabs ? tabs.tabs.map((tab) => tab.tab).join(', ') : 'one page'} title="Tabs">
          {tabs
            ? tabs.tabs.map((tab, index) => (
                <Stack alignItems="center" direction="row" key={tab.id} spacing={0.25}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextSetting label={`Tab ${index + 1}`} onCommit={(title) => title && editor.apply({ op: 'set', id: tab.id, patch: { tab: title } })} value={tab.tab} />
                  </Box>
                  <IconButton aria-label={`Move ${tab.tab} up`} disabled={editor.pending || index === 0} onClick={() => editor.apply({ op: 'move', id: tab.id, before: tabs.tabs[index - 1].id })} size="small">
                    <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton aria-label={`Move ${tab.tab} down`} disabled={editor.pending || index === tabs.tabs.length - 1} onClick={() => editor.apply({ op: 'move', id: tab.id, after: tabs.tabs[index + 1].id })} size="small">
                    <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton aria-label={`Delete the ${tab.tab} tab`} disabled={editor.pending} onClick={() => editor.apply({ op: 'remove', id: tab.id })} size="small">
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              ))
            : null}
          <Actions>
            <Button disabled={editor.pending} onClick={() => editor.apply({ op: 'tab', title: `Tab ${(tabs?.tabs.length ?? 1) + 1}` })} size="small" startIcon={<AddIcon />} variant="text">
              Add a tab
            </Button>
          </Actions>
        </Section>
        <Section id="lens-layout" title="Layout">
          <Actions>
            <Button
              disabled={editor.pending || !blocks.some((block) => block.w)}
              onClick={() => editor.apply(blocks.filter((block) => block.w).map((block) => ({ op: 'set' as const, id: block.id, patch: { w: null } })))}
              size="small"
              variant="text"
            >
              Even out widths
            </Button>
            <Button
              disabled={editor.pending || !blocks.some((block) => block.h || block.size)}
              onClick={() => editor.apply(blocks.filter((block) => block.h || block.size).map((block) => ({ op: 'set' as const, id: block.id, patch: { h: null, size: null } })))}
              size="small"
              variant="text"
            >
              Reset heights
            </Button>
          </Actions>
        </Section>
        <Section id="lens-sections" title="Sections">
          <SectionsEditor data={data} document={document} editor={editor} />
        </Section>
        <Section id="lens-params" summary={Object.keys(document.params ?? {}).length ? `${Object.keys(document.params ?? {}).length}` : 'none'} title="Switches and inputs">
          <ParamsEditor data={data} document={document} editor={editor} />
        </Section>
        {dataPanel ? (
          <Section id="lens-data" summary={`${document.datasets.length} dataset${document.datasets.length === 1 ? '' : 's'}, ${Object.keys(document.measures).length} measure${Object.keys(document.measures).length === 1 ? '' : 's'}`} title="Data">
            <Box sx={{ mx: -2, my: -2 }}>{dataPanel}</Box>
          </Section>
        ) : null}
        <Section id="lens-every" summary="Expert spec editor" title="Every setting">
          <Box sx={{ color: 'text.secondary', fontSize: 12.5 }}>Raw Lens spec fields. Use the named controls above for everyday changes.</Box>
          <RawSettings
            disabled={editor.pending}
            inline
            onSet={(key, value) => editor.apply(replaceSetting('lens', key, value))}
            title="Every Lens setting"
            values={{ title: document.title, lead: document.lead, period: document.period, display: document.display }}
          />
          {Object.entries(document.queries).map(([id, query]) => (
            <RawSettings
              disabled={editor.pending}
              key={id}
              onSet={(key, value) => editor.apply(replaceDefinition('query', id, key, value))}
              title={`Named query ${id}`}
              values={(query ?? {}) as Record<string, unknown>}
            />
          ))}
        </Section>
      </SettingsFind>
    </Box>
  );
}
